/**
 * ContractorSetupsPanel · v2.7.65+E135
 * ══════════════════════════════════════════════════════════════════════
 * Dedicated admin surface for EVERY contractor-setup packet submitted
 * through the private /onboarding/contractor-setup URL — including
 * "orphan" packets that never matched an application record.
 *
 * Why this exists:
 *   - The packet form is unlinked/private, so a submitter can hit it
 *     without ever filling out /drive-with-us. Those packets used to
 *     be invisible in the admin console.
 *   - Additionally, the auto-link logic only matched on exact email.
 *     If a submitter typed the packet email slightly differently from
 *     their application email, the link failed silently. Now the
 *     backend also tries phone-number match on submit, and this panel
 *     lets admins manually link any remaining orphans.
 *
 * Route: sits under User Management → Contractor Packets tab.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, FileText, Inbox, Link2, Link2Off, RefreshCw, Search,
  UserPlus, AlertTriangle, CheckCircle2, Mail, Phone, MapPin,
  X, ClipboardList, ExternalLink,
} from "lucide-react";

const FILTERS = [
  { key: "all",       label: "All",      icon: Inbox },
  { key: "orphan",    label: "Orphaned", icon: AlertTriangle, tint: "amber" },
  { key: "linked",    label: "Linked",   icon: CheckCircle2,  tint: "emerald" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/* ─────────────────────── list row ─────────────────────── */
function SetupRow({ row, active, onClick }) {
  const orphan = !row.application_linked;
  return (
    <button
      onClick={onClick}
      data-testid={`setup-row-${row.id}`}
      className={`w-full text-left rounded-lg border p-3 transition ${
        active
          ? "border-amber-400 bg-amber-500/10"
          : "border-zinc-800 bg-zinc-900/60 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-bold text-zinc-100 truncate">
              {[row.first_name, row.middle_name, row.last_name].filter(Boolean).join(" ") || "(unnamed)"}
            </span>
            {orphan ? (
              <span
                className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-200 border border-amber-400/70"
                title="This packet was submitted but has not been linked to an application yet."
                data-testid={`setup-orphan-tag-${row.id}`}
              >
                ORPHAN
              </span>
            ) : (
              <span
                className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                data-testid={`setup-linked-tag-${row.id}`}
              >
                LINKED
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-400 space-y-0.5">
            <div className="flex items-center gap-1"><Mail className="h-3 w-3"/>{row.email}</div>
            <div className="flex items-center gap-1"><Phone className="h-3 w-3"/>{row.driver_phone}</div>
            {(row.city || row.state) && (
              <div className="flex items-center gap-1"><MapPin className="h-3 w-3"/>{[row.city, row.state].filter(Boolean).join(", ")}</div>
            )}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold whitespace-nowrap">
          {fmt(row.created_at).split(",")[0]}
        </span>
      </div>
    </button>
  );
}

/* ─────────────────────── link modal ─────────────────────── */
function LinkOrphanModal({ setupId, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(null);

  const search = useCallback(async (term) => {
    if (!term || term.trim().length < 2) { setApps([]); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.get(`/admin/applications?q=${encodeURIComponent(term.trim())}&limit=15`);
      setApps(data?.results || data?.applications || data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    const h = setTimeout(() => search(q), 300);
    return () => clearTimeout(h);
  }, [q, search]);

  const linkTo = async (appId) => {
    setLinking(appId);
    try {
      await adminApi.post(`/contractor-setup/admin/${setupId}/link`, { application_id: appId });
      toast.success("Packet linked to application");
      onLinked(appId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setLinking(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="link-orphan-modal">
      <div className="bg-zinc-900 border-2 border-amber-400/50 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-amber-400"/>
            <h3 className="text-lg font-bold text-zinc-100">Link Packet to Application</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="link-modal-close">
            <X className="h-4 w-4"/>
          </Button>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500"/>
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search applications by name / email / phone…"
              className="pl-9 bg-zinc-950 border-zinc-700 text-zinc-100"
              data-testid="link-modal-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
          {busy && <div className="text-center py-6 text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/>Searching…</div>}
          {!busy && q.trim().length < 2 && <div className="text-center py-6 text-zinc-500 text-sm">Type at least 2 characters to search.</div>}
          {!busy && q.trim().length >= 2 && apps.length === 0 && <div className="text-center py-6 text-zinc-500 text-sm">No applications match &quot;{q}&quot;.</div>}
          {apps.map((a) => (
            <div key={a.id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-zinc-100">{a.first_name} {a.last_name}</div>
                <div className="text-xs text-zinc-400 flex flex-wrap gap-3">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3"/>{a.email}</span>
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3"/>{a.cell_phone || a.phone}</span>
                  <span className="text-zinc-500 uppercase">· {a.position || a.applying_for || "—"}</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => linkTo(a.id)}
                disabled={linking === a.id}
                data-testid={`link-modal-apply-${a.id}`}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              >
                {linking === a.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Link2 className="h-3 w-3 mr-1"/>Link</>}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── detail pane ─────────────────────── */
function SetupDetail({ setupId, onChanged }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const reload = useCallback(async () => {
    if (!setupId) { setSetup(null); return; }
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/contractor-setup/admin/${setupId}`);
      setSetup(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load packet");
    } finally { setLoading(false); }
  }, [setupId]);

  useEffect(() => { reload(); }, [reload]);

  const unlink = async () => {
    if (!window.confirm("Detach this packet from its application?")) return;
    try {
      await adminApi.post(`/contractor-setup/admin/${setupId}/unlink`);
      toast.success("Packet unlinked");
      onChanged?.();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  if (!setupId) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-500" data-testid="setup-detail-empty">
        <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50"/>
        Select a packet from the list to view its details.
      </div>
    );
  }

  if (loading || !setup) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-400" data-testid="setup-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading packet…
      </div>
    );
  }

  const orphan = !setup.linked_application_id;
  const fullName = [setup.first_name, setup.middle_name, setup.last_name].filter(Boolean).join(" ");

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3" data-testid="setup-detail-pane">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xl font-bold text-zinc-100">{fullName}</h3>
          <div className="text-xs text-zinc-400 mt-0.5">Submitted {fmt(setup.created_at)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {orphan ? (
            <Button
              size="sm"
              onClick={() => setShowLink(true)}
              data-testid="setup-link-btn"
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
            >
              <UserPlus className="h-4 w-4 mr-1"/>Link to Application
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <a
                href={`/admin#sub=users-applications&app=${setup.linked_application_id}`}
                className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-2 py-1 rounded-full font-bold hover:bg-emerald-500/30"
                data-testid="setup-open-application"
              >
                <ExternalLink className="h-3 w-3"/>Open Application
              </a>
              <Button
                size="sm" variant="ghost"
                onClick={unlink}
                data-testid="setup-unlink-btn"
                className="text-rose-400 hover:text-rose-300"
              >
                <Link2Off className="h-3.5 w-3.5 mr-1"/>Unlink
              </Button>
            </div>
          )}
        </div>
      </div>

      {orphan && (
        <div className="rounded-lg border-2 border-amber-400/50 bg-amber-500/10 p-3 flex items-start gap-2 text-sm text-amber-100" data-testid="setup-orphan-banner">
          <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold">This packet is not linked to any application.</div>
            <div className="text-xs mt-0.5">
              The submitter used <span className="font-mono">{setup.email}</span> and phone {setup.driver_phone} which didn&apos;t match any /drive-with-us application on file. Click <span className="font-bold">Link to Application</span> above to attach it to the correct applicant.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Contact</div>
          <div className="text-sm space-y-1">
            <div><Mail className="inline h-3 w-3 mr-1 text-zinc-500"/>{setup.email}</div>
            <div><Phone className="inline h-3 w-3 mr-1 text-zinc-500"/>{setup.driver_phone}</div>
            <div><MapPin className="inline h-3 w-3 mr-1 text-zinc-500"/>{setup.address}, {setup.city}, {setup.state} {setup.zip}</div>
          </div>
        </div>
        <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Sensitive (masked)</div>
          <div className="text-sm space-y-1 font-mono">
            <div>SSN: ***-**-<span className="text-amber-300">{setup.ssn_last4}</span></div>
            <div>DL: {setup.dl_number} · {setup.dl_issuing_state} · exp {setup.dl_expiration_date}</div>
            <div>Bank tail-4: <span className="text-amber-300">{setup.banking?.account_last4 || "—"}</span></div>
          </div>
        </div>
      </div>

      {Array.isArray(setup.documents) && setup.documents.length > 0 && (
        <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Uploaded Documents</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {setup.documents.map((d, i) => (
              <a
                key={`${d.slot}-${i}`}
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded border border-zinc-800 bg-zinc-900 hover:border-amber-400 p-2 text-xs text-center flex flex-col items-center gap-1"
                data-testid={`setup-doc-${d.slot}`}
              >
                <FileText className="h-5 w-5 text-amber-400"/>
                <div className="font-bold uppercase">{d.slot.replace("_", " ")}</div>
                {d.doc_type && <div className="text-[10px] text-zinc-500">{d.doc_type}</div>}
              </a>
            ))}
          </div>
        </div>
      )}

      {setup.signature_pdf_url && (
        <a
          href={setup.signature_pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold px-3 py-2 text-sm"
          data-testid="setup-signed-release"
        >
          <FileText className="h-4 w-4"/>View Signed Background-Release PDF
        </a>
      )}

      {showLink && (
        <LinkOrphanModal
          setupId={setupId}
          onClose={() => setShowLink(false)}
          onLinked={() => {
            setShowLink(false);
            reload();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────── panel shell ─────────────────────── */
export default function ContractorSetupsPanel() {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ count: 0, linked: 0, orphaned: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === "orphan") params.set("linked", "false");
      if (filter === "linked") params.set("linked", "true");
      if (q.trim()) params.set("q", q.trim());
      const { data } = await adminApi.get(`/contractor-setup/admin/list?${params.toString()}`);
      setRows(data.results || []);
      setCounts({ count: data.count, linked: data.linked, orphaned: data.orphaned });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load contractor packets");
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await adminApi.post("/contractor-setup/admin/backfill-links");
      toast.success(`Backfill complete — linked ${data.linked} of ${data.scanned} orphans.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const activeFilterDef = FILTERS.find((f) => f.key === filter);
  const showEmpty = !loading && rows.length === 0;

  return (
    <div className="space-y-3" data-testid="contractor-setups-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 text-center">
          <div className="text-2xl font-black text-zinc-100" data-testid="setups-total-count">{counts.count}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">TOTAL PACKETS</div>
        </div>
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 text-center">
          <div className="text-2xl font-black text-emerald-300" data-testid="setups-linked-count">{counts.linked}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-bold mt-1">LINKED</div>
        </div>
        <div className="rounded-lg border-2 border-amber-400/70 bg-amber-500/10 p-3 text-center">
          <div className="text-2xl font-black text-amber-300" data-testid="setups-orphan-count">{counts.orphaned}</div>
          <div className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold mt-1">ORPHANED</div>
        </div>
      </div>

      {/* Filter + search + backfill */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-zinc-900/60 border border-zinc-800 rounded-lg p-1 gap-0.5" data-testid="setups-filter">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`setups-filter-${f.key}`}
                className={`text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5 transition ${
                  active
                    ? "bg-amber-500 text-black"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                <Icon className="h-3 w-3"/>{f.label}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email · name · phone…"
            className="pl-9 h-9 bg-zinc-950 border-zinc-700 text-zinc-100"
            data-testid="setups-search"
          />
        </div>
        <Button
          size="sm" variant="outline"
          onClick={runBackfill}
          disabled={backfilling}
          data-testid="setups-backfill-btn"
          className="border-zinc-700 text-zinc-200"
          title="Re-scan every orphan and try to auto-link it by email OR phone"
        >
          {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <RefreshCw className="h-3.5 w-3.5 mr-1"/>}
          Auto-Link Orphans
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="space-y-2 lg:col-span-1">
          {loading && (
            <div className="text-center py-6 text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/>Loading…</div>
          )}
          {showEmpty && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 text-center text-zinc-500" data-testid="setups-empty">
              <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40"/>
              No {activeFilterDef?.label.toLowerCase()} packets to show.
            </div>
          )}
          {rows.map((r) => (
            <SetupRow
              key={r.id}
              row={r}
              active={selectedId === r.id}
              onClick={() => setSelectedId(r.id)}
            />
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          <SetupDetail
            setupId={selectedId}
            onChanged={load}
          />
        </div>
      </div>
    </div>
  );
}
